import { formatDistanceToNow } from 'date-fns';
import { cn } from '../../lib/utils';
import { NotificationIcon } from './NotificationIcon';
import type { NotificationResponse } from '../../types/notifications';
import { getNotificationTypeLabel } from '../../types/notifications';

interface NotificationItemProps {
  notification: NotificationResponse;
  onClick?: (notification: NotificationResponse) => void;
  onMarkAsRead?: (id: string) => void;
  compact?: boolean;
}

/**
 * Renders a single notification item
 */
export function NotificationItem({
  notification,
  onClick,
  onMarkAsRead,
  compact = false,
}: NotificationItemProps) {
  const handleClick = () => {
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
    if (onClick) {
      onClick(notification);
    }
  };

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 hover:bg-muted/50 cursor-pointer transition-colors',
        !notification.isRead && 'bg-blue-50/50 dark:bg-blue-950/20',
        compact && 'py-2'
      )}
      onClick={handleClick}
    >
      {/* Unread indicator */}
      <div className="flex-shrink-0 mt-1">
        {!notification.isRead ? (
          <div className="h-2 w-2 rounded-full bg-blue-500" />
        ) : (
          <div className="h-2 w-2" />
        )}
      </div>

      {/* Icon */}
      <div className="flex-shrink-0 mt-0.5">
        <NotificationIcon type={notification.type} className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-xs font-medium text-muted-foreground',
              !notification.isRead && 'text-blue-600 dark:text-blue-400'
            )}
          >
            {getNotificationTypeLabel(notification.type)}
          </span>
          <span className="text-xs text-muted-foreground">&middot;</span>
          <span className="text-xs text-muted-foreground">{timeAgo}</span>
        </div>
        <p
          className={cn(
            'text-sm font-medium',
            compact && 'line-clamp-1',
            !notification.isRead && 'text-foreground',
            notification.isRead && 'text-muted-foreground'
          )}
        >
          {notification.title}
        </p>
        {notification.message && (
          <p className={cn(
            "text-xs text-muted-foreground mt-0.5",
            compact && "line-clamp-2"
          )}>
            {notification.message}
          </p>
        )}
      </div>
    </div>
  );
}
